import { memo } from 'react'
import { Zap } from 'lucide-react'

interface PixelatedLoaderProps {
  size?: 'sm' | 'md' | 'lg'
}

export const PixelatedLoader = memo(function PixelatedLoader({ size = 'md' }: PixelatedLoaderProps) {
  const sizeClasses = {
    sm: 'size-16',
    md: 'size-24',
    lg: 'size-32',
  }

  const iconSizes = {
    sm: 'size-8',
    md: 'size-12',
    lg: 'size-16',
  }

  return (
    <div className="relative inline-flex items-center justify-center">
      {/* Outer glow rings */}
      <div className="absolute inset-0 rounded-2xl bg-violet-500/10 animate-pulse-slow" />
      <div 
        className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-violet-500/5 via-purple-500/10 to-violet-500/5 blur-xl animate-glow-rotate"
      />
      
      {/* Main container with pixelation effect */}
      <div 
        className={`relative ${sizeClasses[size]} rounded-2xl bg-gradient-to-br from-[#1a1a2e] to-[#0f0f1a] border border-violet-500/30 overflow-hidden flex items-center justify-center animate-pixel-breathe`}
      >
        {/* Animated grid overlay for pixel effect */}
        <div className="absolute inset-0 opacity-30 animate-grid-fade">
          <div 
            className="w-full h-full"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(139, 92, 246, 0.3) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(139, 92, 246, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '8px 8px',
            }}
          />
        </div>

        {/* Scanning line effect */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-full h-1 bg-gradient-to-r from-transparent via-violet-400/50 to-transparent animate-scan-line" />
        </div>

        {/* Icon container with pixelation animation */}
        <div className="relative z-10 animate-icon-pixel">
          <Zap className={`${iconSizes[size]} text-violet-400 fill-violet-400/20`} />
        </div>

        {/* Corner accents */}
        <div className="absolute top-2 left-2 w-2 h-2 border-l-2 border-t-2 border-violet-500/50" />
        <div className="absolute top-2 right-2 w-2 h-2 border-r-2 border-t-2 border-violet-500/50" />
        <div className="absolute bottom-2 left-2 w-2 h-2 border-l-2 border-b-2 border-violet-500/50" />
        <div className="absolute bottom-2 right-2 w-2 h-2 border-r-2 border-b-2 border-violet-500/50" />
      </div>

      {/* Orbiting particles */}
      <div className="absolute inset-0 animate-spin-slow">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-1.5 h-1.5 rounded-full bg-violet-400/80 blur-[1px]" />
      </div>
      <div className="absolute inset-0 animate-spin-slow-reverse">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-2 w-1 h-1 rounded-full bg-purple-400/60 blur-[1px]" />
      </div>
    </div>
  )
})
